import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Slider from 'react-slick';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

import MysteryRevealGamesArrow from 'Components/Order/KeyReveals/MysteryRevealGamesArrow';
import MysteryRevealCard from 'Components/Order/KeyReveals/MysteryRevealCard';
import useViewport from 'Hooks/useViewport';
import CarouselPaging from 'Components/Carousel/CarouselPaging';

import 'Components/Order/KeyReveals/MysteryRevealGames.scss';

function MysteryRevealGames({ item, revealedOrder, template, viewKeys, testReveal, testGames }) {
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { isBelowXl } = useViewport();

  let allGames = [];

  if (!testReveal && !isEmpty(revealedOrder) && item.type === 'bundle') {
    const bundleItem = revealedOrder.items.find(orderItem => orderItem.iid === item.iid);
    bundleItem.bundles.forEach((tier) => {
      if (tier.games) {
        tier.games.forEach((game) => {
          if (game) {
            allGames = allGames.concat(game);
          }
        });
      }
    });
  }

  if (!testReveal && item.type === 'game') {
    allGames.push(item);
  }

  if (testReveal && !isEmpty(testGames)) allGames = testGames;

  // Testing the carousel when not linked to a mystery order
  // if (true) {
  //   item.bundles.forEach((tier) => {
  //     if (tier.games) {
  //       tier.games.forEach((game) => {
  //         if (game) {
  //           allGames = allGames.concat(game);
  //         }
  //       });
  //     }
  //   });
  // }

  const useCarousel = allGames.length > 3 || (allGames.length <= 3 && isBelowXl);
  // const useCarousel = false;

  const flipSpeed = 1600;

  useEffect(() => {
    setTimeout(() => {
      if (!loaded) setLoaded(true);
      if (!useCarousel && count < 3) setCount(count + 1);
    }, flipSpeed / 2);
  }, [count, loaded, useCarousel]);

  const sliderRef = useRef();

  const pauseCarousel = () => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.slickPause();
    }
  };

  const nextSlide = () => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.slickNext();
      sliderRef.current.slickPause();
    }
  };

  const previousSlide = () => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.slickPrev();
      sliderRef.current.slickPause();
    }
  };

  const settings = {
    arrows: true,
    infinite: true,
    prevArrow: <MysteryRevealGamesArrow
      ariaLabel="Previous"
      arrowDirection="left"
      containerClassName="MysteryRevealGames__gamesSlider__arrows"
      clickFunction={() => { previousSlide(); }}
    />,
    nextArrow: <MysteryRevealGamesArrow
      ariaLabel="Next"
      arrowDirection="right"
      containerClassName="MysteryRevealGames__gamesSlider__arrows"
      clickFunction={() => { nextSlide(); }}
    />,
    afterChange: (i) => {
      if ((i + 1 > count) && (count !== allGames.length)) {
        setCount(i + 1);
      }
      if (i + 1 >= allGames.length) {
        pauseCarousel();
      }
    },
    autoplay: true,
    autoplaySpeed: flipSpeed,
    slidesToShow: 3,
    slidesPerRow: 1,
    slidesToScroll: 1,
    centerMode: true,
    dots: true,
    touchMove: true,
    pauseOnFocus: true,
    pauseOnHover: false,
    swipeToSlide: true,
    accessibility: true,
    customPaging: i => (
      <CarouselPaging
        slideIndex={i}
        slideLength={Array.isArray(allGames) ? allGames.length : 0}
        slidesToShow={3}
      />
    ),
    responsive: [
      {
        breakpoint: 1439,
        settings: {
          slidesToShow: 3,
          slidesPerRow: 1,
          slidesToScroll: 1,
          centerPadding: '0px',
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(allGames) ? allGames.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
      {
        breakpoint: 1199,
        settings: {
          slidesToShow: 1,
          slidesPerRow: 1,
          slidesToScroll: 1,
          centerPadding: '200px',
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(allGames) ? allGames.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesPerRow: 1,
          slidesToScroll: 1,
          centerPadding: '220px',
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(allGames) ? allGames.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
      {
        breakpoint: 767,
        settings: {
          arrows: false,
          slidesToShow: 1,
          slidesPerRow: 1,
          slidesToScroll: 1,
          centerPadding: '115px',
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(allGames) ? allGames.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
      {
        breakpoint: 575,
        settings: {
          arrows: false,
          slidesToShow: 1,
          slidesPerRow: 1,
          slidesToScroll: 1,
          centerPadding: '30px',
          customPaging: i => (
            <CarouselPaging
              slideIndex={i}
              slideLength={Array.isArray(allGames) ? allGames.length : 0}
              slidesToShow={1}
            />
          ),
        },
      },
    ],
  };

  return (
    <div className="MysteryRevealGames">
      <div className="MysteryRevealGames__topText">
        {!testReveal && allGames.length === 1 &&
          <FormattedMessage
            id="mystery-reveal-games.title.game"
            defaultMessage="Enjoy your game!"
          />
        }
        {!testReveal && allGames.length > 1 &&
          <FormattedMessage
            id="mystery-reveal-games.title.games"
            defaultMessage="Enjoy your games!"
          />
        }
        {testReveal &&
          <FormattedMessage
            id="mystery-reveal-games.title.test"
            defaultMessage="Now you’ve tried it, why not get your own mystery piñata and see what games you’ll find inside!"
          />
        }
      </div>
      <div className="MysteryRevealGames__gamesContainer">
        {!useCarousel &&
          <div className="MysteryRevealGames__gamesGrid">
            {allGames.slice(0, 3).map((game, i) =>
              <div className="MysteryRevealGames__gamesCol" key={game._id}>
                <MysteryRevealCard
                  game={game}
                  cardNumber={i}
                  count={count}
                  totalCards={allGames.length}
                  loaded={loaded}
                />
              </div>,
            )}
          </div>
        }

        {useCarousel &&
          <Slider
            {...settings}
            className={classNames(
              'MysteryRevealGames__gamesSlider',
              { 'MysteryRevealGames__gamesSlider--fiesta': template === 'pinata' },
            )}
            ref={sliderRef}
          >
            {allGames.map((game, i) =>
              <MysteryRevealCard
                game={game}
                cardNumber={i}
                count={count}
                totalCards={allGames.length}
                loaded={loaded}
                key={game._id}
              />,
            )}
          </Slider>
        }
      </div>

      {!testReveal &&
        <button
          className={classNames(
            'MysteryReveal__revealButton',
            { 'MysteryReveal__revealButton--fiesta': template === 'pinata' },
          )}
          onClick={() => { viewKeys(); }}
        >
          <FormattedMessage id="mystery-reveal-games.button.view-keys" defaultMessage="View your keys" />
        </button>
      }
    </div>
  );
}

MysteryRevealGames.propTypes = {
  item: PropTypes.shape({}),
  revealedOrder: PropTypes.shape({}),
  template: PropTypes.string,
  viewKeys: PropTypes.func,
  testReveal: PropTypes.bool,
  testGames: PropTypes.arrayOf(PropTypes.shape({})),
};

MysteryRevealGames.defaultProps = {
  item: {},
  revealedOrder: {},
  template: '',
  viewKeys: () => {},
  testReveal: false,
  testGames: [],
};

export default MysteryRevealGames;
